import merge from 'lodash/merge';
import { routerRef } from '@/routerHolder';
import { addMany } from './helpers';

const state = {
  allIds: [],
  byId: {},
  currentVenueId: null // Add tracking for current venue
};

const getGalleryImage = venue => {
  const idx = venue?.media?.findIndex(m => m.location === 'Header');
  return idx >= 0 ? venue.media[idx].url : null;
};

const hasPendingTicketSelection = venue =>
  (venue?.products || []).some(({ order_products: orderProducts = [] }) =>
    orderProducts.some(({ ticket }) => ticket?.status === 'pending_date_selection')
  );

const getters = {
  find: (state, getters, _, rootGetters) => (id, dealId) => {
    const products = rootGetters['products/byVenueId'](id, dealId);
    const venue = {
      ...state.byId[id],
      products,
      schedulingRequired: hasPendingTicketSelection({ products })
    };
    venue.hasSomeRedemption = venue.products.some(
      ({ redeemedProductsCount }) => redeemedProductsCount > 0
    );
    return venue;
  },

  getCategoriesByVenueIds: (state, getters, _, rootGetters) => venueIds => {
    const categoriesById = new Map();
    venueIds.forEach(venueId => {
      const venueCategories = state.byId[venueId]?.categories || [];
      venueCategories.forEach(category => {
        const resolved =
          category && typeof category === 'object' && category.id != null
            ? category
            : rootGetters['categories/find'](category);
        if (resolved?.id != null) {
          categoriesById.set(resolved.id, resolved);
        }
      });
    });
    return [...categoriesById.values()];
  },

  findAll: (_, getters) => (ids, dealId) =>
    getters.list(dealId).filter(venue => ids?.includes(venue.id)) || [],

  findByDealId: (state, getters, _, rootGetters) => dealId =>
    getters.findAll(rootGetters['deals/find'](dealId).venue_ids, dealId),

  list: (state, getters) => dealId =>
    state.allIds.map(id => getters.find(id, dealId)),

  pointsAvailable: () => venue =>
    venue?.products?.reduce(
      (pAcc, p) => (!p.redeemed ? pAcc + p.points_to_be_earned : pAcc),
      0
    ) || 0,

  withPoints: (state, getters) => dealId =>
    getters
      .findByDealId(dealId)
      .filter(v => v?.products?.some(p => p?.points_to_be_earned > 0)),

  getCurrentVenueId: state => state.currentVenueId
};

const actions = {
  addVenues: ({ commit }, venues) => {
    venues.forEach(venue => {
      venue.gallery_bg = getGalleryImage(venue);
    });
    commit('addMany', venues);
  },
  navigateToVenue({ commit }, { venueId, dealId, token, query }) {
    commit('setCurrentVenueId', venueId);

    const dealPath = dealId ? `/deal/${dealId}` : '';
    const venuePath = `/experiences${dealPath}/venue/${venueId}`;

    window.history.back();

    setTimeout(() => {
      const q = query && typeof query === 'object' ? { ...query } : token ? { t: token } : {};
      if (token != null && token !== '' && q.t == null) {
        q.t = token;
      }
      routerRef.current?.push({
        path: venuePath,
        query: q
      });
    }, 50);
  },
  setVenueDistance: ({ commit }, venue) => commit('setDistance', venue),
  setCurrentVenueId: ({ commit }, venueId) => commit('setCurrentVenueId', venueId),
  clearCurrentVenueId: ({ commit }) => commit('setCurrentVenueId', null)
};

const mutations = {
  add: (state, venue) => {
    // In the case of venues the products array is basic, so if the
    // id already exists we will not overwrite it here, just return
    if (!state.allIds.includes(venue.id)) {
      state.byId[venue.id] = { data: {}, ...venue };
      state.allIds.push(venue.id);
    } else {
      const merged = merge(state.byId[venue.id], venue);
      state.byId[venue.id] = merged;
    }
  },
  addMany: (state, items) => addMany(state, items),
  setDistance: (state, venue) => {
    if (state.byId[venue.id]) {
      state.byId[venue.id].miles = venue.miles;
    }
  },
  setGalleryImage: (state, venue) => {
    const currentVenue = state.byId[venue.id];
    const idx = currentVenue?.media?.findIndex(m => m.location === 'Header');
    if (currentVenue) {
      state.byId[currentVenue.id].gallery_bg =
        idx >= 0 ? currentVenue.media[idx].url : null;
    }
  },
  setCurrentVenueId: (state, venueId) => {
    state.currentVenueId = venueId;
  }
};

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations
};
